import { Injectable } from '@angular/core';
import {
  BASE_API_URL,
  IDtoListingBlackoutTimes,
  IDtoListingBooking, IDtoListingDiscount, IDtoListingLocation, IDtoSiteAmenities,
  IDtoSiteName,
  ILandType,
  IListing,
  IListingExtra,
  IListingWhere, IPhoto,
  IPolicy,
  ISignalReception,
  IUser,
  Listing
} from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { CachedHttpClient, ListingDbSet, ListingPhotosDbSet } from '@curbnturf/network';
import { Observable, of } from 'rxjs';

const API_URL = BASE_API_URL + 'listing';

@Injectable({
  providedIn: 'root',
})
export class ListingService {
  constructor(
    private http: CachedHttpClient,
    private listingDbSet: ListingDbSet,
    private listingPhotosDbSet: ListingPhotosDbSet,
  ) {}

  create(listing: IListing): Observable<IListing> {
    return this.http.post<IListing>(API_URL, this.formatForServer(listing));
  }

  createPolicy(listing: IListing): Observable<IListing> {
    const policies: IPolicy[] = [];
    if (listing.policies) {
      policies.push(...listing.policies);
    }

    policies.forEach((el) => (el.listing = listing));

    return this.update({ ...listing, policies });
  }

  createSignalReception(listing: IListing): Observable<IListing> {
    const signalReception: ISignalReception[] = [];
    if (listing.signalReception) {
      signalReception.push(...listing.signalReception);
    }

    signalReception.forEach((el) => (el.listing = listing));

    return this.update({
      ...listing,
      signalReception,
    });
  }

  fetch(listingId: number): Observable<IListing> {
    return this.http.get<IListing>(`${API_URL}/${listingId}`);
  }

  fetchAll(
    query: IListingWhere,
    sort: string = 'id',
    page: number = 1,
    pageSize: number = 100,
  ): Observable<{ total: number; page: number; listings: IListing[] }> {
    const queryString = objectToUrlParams({ ...query, sort, page, pageSize });
    return this.http.get<{ total: number; page: number; listings: IListing[] }>(`${API_URL}?${queryString}`);
  }

  findListingForSite(siteId: number): Observable<IListing> {
    return this.http.get<IListing>(`${API_URL}/find-listing-for-site/${siteId}`);
  }

  remove(listingId: number): Observable<IListing> {
    return this.http.delete<IListing>(`${API_URL}/${listingId}`);
  }

  restore(listingId: number): Observable<IListing> {
    return this.http.post<IListing>(`${API_URL}/${listingId}/restore`, null);
  }

  removePolicy(listing: IListing, policy: IPolicy): Observable<IListing> {
    if (!listing.policies) {
      return of(listing);
    }
    const policies = [...listing.policies];

    const index = policies.findIndex((indexedPolicy) => indexedPolicy.id === policy.id);

    if (index >= 0) {
      policies.splice(index, 1);
    }

    return this.update({ ...listing, policies });
  }

  removeSignalReception(listing: IListing, signalReception: ISignalReception): Observable<IListing> {
    if (!listing.signalReception) {
      return of(listing);
    }

    const signalReceptions = [...listing.signalReception];

    const index = signalReceptions.indexOf(signalReception);

    if (index || index === 0) {
      signalReceptions.splice(index, 1);
    }

    return this.update({
      ...listing,
      signalReception: signalReceptions,
    });
  }

  upsert(listing: IListing): Observable<IListing> {
    if (listing.id) {
      return this.update(listing);
    } else {
      return this.create(new Listing(listing));
    }
  }

  update(listing: IListing): Observable<IListing> {
    if (!listing.id) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listing.id}`, this.formatForServer(listing));
  }

  updateLocation(listingId: number, listingDto: IDtoListingLocation): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/location`, listingDto);
  }

  updateBooking(listingId: number, listingDto: IDtoListingBooking): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/booking`, listingDto);
  }

  updatePropertyType(listingId: number, types: ILandType['type'][]): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/property-types`, { types });
  }

  updatePotentialHazards(
    listingId: number,
    potentialHazards: boolean,
    potentialHazardDescription: string,
  ): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/potential-hazards`, {
      potentialHazards,
      potentialHazardDescription,
    });
  }

  updateAmenities(listingId: number, siteId: number, data: IDtoSiteAmenities): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/amenities/${siteId}`, data);
  }

  updatePolicies(listingId: number, policies: IPolicy[]): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/policies/`, policies);
  }

  updateExtras(listingId: number, extras: IListingExtra[]): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/extras/`, extras);
  }

  updateBlackoutTimes(listingId: number, blackoutTimes: IDtoListingBlackoutTimes): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/blackout-times/`, blackoutTimes);
  }

  updateDescription(listingId: number, data: IDtoSiteName): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/description/`, data);
  }

  updatePhotos(listingId: number, photos: IPhoto[]) {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/photo-details/`, photos);
  }

  updateDiscount(listingId: number, data: IDtoListingDiscount): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/price`, data);
  }

  addNewSites(listingId: number, numberOfSites: number): Observable<IListing> {
    if (!listingId) {
      throw new Error('Missing Listing Identifier');
    }
    return this.http.put<IListing>(`${API_URL}/${listingId}/add-site`, { numberOfSites: numberOfSites });
  }

  enable(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/enable`, null);
  }

  enableAndHide(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/enableAndHide`, null);
  }

  disable(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/disable`, null);
  }

  hide(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/hide`, null);
  }

  show(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/show`, null);
  }

  removeImage(listingId: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${listingId}/remove-image/${photoIndex}`);
  }

  uploadImage(listingId: number, formData: FormData): Observable<{ id: number; photos: IPhoto[] }> {
    return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${listingId}/upload-image`, formData);
  }

  favorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/favorite`, null);
  }

  unfavorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/unfavorite`, null);
  }

  /**
   * Retrieves owned listings from the local SQL database
   * @param {IUser} user
   */
  async retrieveLocalOwnedListings(user: IUser): Promise<IListing[]> {
    const listings = await this.listingDbSet.retrieveOwnListings(user?.id);
    const listingIds: number[] = listings.filter((el) => el !== undefined).map((el) => el.id as number);

    // Get the photo records
    const photoResults = await this.listingPhotosDbSet.retrieveBatch(listingIds);
    listings.forEach((listing: IListing) => {
      listing.photos = [...photoResults.filter((photo) => photo.listingId === listing.id)];
    });

    return listings;
  }

  private formatForServer(listing: IListing): IListing {
    // remove sites from the listing, we will modify these elsewhere.
    return { ...listing, sites: undefined };
  }
}
